import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { requestApprovalsApi, type RequestApproval } from './api'
import type { ListParams } from '@/lib/api/types'
import { useToast } from '@/hooks/use-toast'

export const requestApprovalKeys = {
    all: ['request-approvals'] as const,
    lists: () => [...requestApprovalKeys.all, 'list'] as const,
    list: (params?: ListParams) => [...requestApprovalKeys.lists(), params] as const,
}

export function useRequestApprovals(params?: ListParams) {
    return useQuery({
        queryKey: requestApprovalKeys.list(params),
        queryFn: () => requestApprovalsApi.list(params),
        enabled: true,
    })
}

export function useApproveRequestApproval() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (requestID: string) => requestApprovalsApi.approve(requestID),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: requestApprovalKeys.all })
            toast({
                title: 'Request approved',
                description: 'The retrieval request has been successfully approved.',
            })
        },
        onError: (error: any) => {
            toast({
                title: 'Failed to approve request',
                description: error?.response?.data?.message || error.message || 'An error occurred while approving the request.',
                variant: 'destructive',
            })
        },
    })
}

export function useDeleteRequestApproval() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (requestID: string) => requestApprovalsApi.delete(requestID),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: requestApprovalKeys.all })
            toast({
                title: 'Request deleted',
                description: 'The retrieval request has been successfully deleted.',
            })
        },
        onError: (error: any) => {
            toast({
                title: 'Failed to delete request',
                description: error?.response?.data?.message || error.message || 'An error occurred while deleting the request.',
                variant: 'destructive',
            })
        },
    })
}
